import CardAnimations from './CardAnimations.js';

/**
 * DeckRenderer class handles the visual representation of a deck of cards using PixiJS.
 * 
 * Features:
 * - Renders all cards in a stacked pile with slight offset for depth effect
 * - Animates card extraction and reveal
 * - Manages card positions and animations
 */
class DeckRenderer {
    constructor(app, deck) {
        this.app = app;
        this.deck = deck;
        this.container = null;
        this.cardSprites = [];
        this.cardWidth = 100;
        this.cardHeight = 150;
        this.stackOffsetX = 0.2; // Slight horizontal offset for stacking effect
        this.stackOffsetY = 0.2; // Slight vertical offset for stacking effect
        this.centerX = (app && app.screen) ? app.screen.width / 2 : 0;
        this.centerY = (app && app.screen) ? app.screen.height / 2 : 0;
        this.rotation = 0;
    }

    /**
     * Gets the card image path based on suit and value
     * Format: XYY where X is suit (1-4) and YY is value (01-12)
     * Suits: 1=oros, 2=copas, 3=espadas, 4=bastos
     * Values: 01-07, 10-12
     */
    getCardImagePath(suit, value) {
        const suitMap = {
            'oros': 1,
            'copas': 2,
            'espadas': 3,
            'bastos': 4
        };

        const suitCode = suitMap[suit];
        const valueCode = value.toString().padStart(2, '0');

        return `assets/images/deck/${suitCode}${valueCode}.png`;
    }

    /**
     * Gets the card back image path
     */
    getCardBackPath() {
        const cardBack = localStorage.getItem('cardBack') || '001';
        return `assets/images/deck/${cardBack}.png`;
    }

    /**
     * Renders the complete deck as a stacked pile
     * Cards are rendered from bottom to top, with the last card on top
     */
    async render() {
        const { Container, Sprite, Assets } = await import('pixi.js');

        // Clean up existing container if it exists
        if (this.container) {
            this.container.destroy({ children: true });
            this.cardSprites = [];
        }

        // Create a container for the deck
        this.container = new Container();
        this.container.label = 'DeckContainer';

        // Apply position and rotation to the container
        this.container.x = this.centerX;
        this.container.y = this.centerY;
        this.container.rotation = this.rotation;

        this.app.stage.addChild(this.container);

        // Load card back texture
        const cardBackTexture = await Assets.load(this.getCardBackPath());

        // Render all cards in the deck
        // We render them in reverse order so the first card (index 0) appears on top
        for (let i = this.deck.cards.length - 1; i >= 0; i--) {
            const card = this.deck.cards[i];

            // Create sprite for card back
            const cardSprite = new Sprite(cardBackTexture);
            cardSprite.label = `Card_${i}_${card.suit}_${card.value}`;
            cardSprite.width = this.cardWidth;
            cardSprite.height = this.cardHeight;
            cardSprite.anchor.set(0.5);

            // Position with stacking effect (relative to container center 0,0)
            // Cards at the bottom of the array (higher index) are rendered lower in the stack
            const stackIndex = this.deck.cards.length - 1 - i;
            cardSprite.x = (stackIndex * this.stackOffsetX);
            cardSprite.y = (stackIndex * this.stackOffsetY);

            // Store reference to the card data
            cardSprite.cardData = card;
            cardSprite.deckIndex = i;

            this.container.addChild(cardSprite);
            this.cardSprites.push(cardSprite);
        }

        return this.container;
    }

    /**
     * Animates extracting and revealing a card from the top of the deck
     * The card moves in the specified direction and flips to show its face
     * 
     * @param {number} cardIndex - Index of the card in the deck (default: 0 for top card)
     * @param {number} revealDistance - How far to move the card (default: 200)
     * @param {string} direction - Direction to move: 'up', 'down', 'left', 'right' (default: 'up')
     * @returns {Promise<Object>} Resolves with the revealed card data
     */
    async extractAndRevealCard(cardIndex = 0, revealDistance = 200, direction = 'up') {
        if (!this.cardSprites || this.cardSprites.length === 0) {
            throw new Error('Deck not rendered yet');
        }

        // Find the sprite for this card
        // Since we rendered in reverse order, we need to find by deckIndex
        const cardSprite = this.cardSprites.find(sprite => sprite.deckIndex === cardIndex);

        if (!cardSprite) {
            throw new Error(`Card at index ${cardIndex} not found`);
        }

        const { Assets } = await import('pixi.js');
        const card = cardSprite.cardData;

        // Step 1: Animate card moving in the specified direction (relative to container)
        let targetX = cardSprite.x;
        let targetY = cardSprite.y;
        
        switch (direction) {
            case 'up':
                targetY = cardSprite.y - revealDistance;
                break;
            case 'down':
                targetY = cardSprite.y + revealDistance;
                break;
            case 'left':
                targetX = cardSprite.x - revealDistance;
                break;
            case 'right':
                targetX = cardSprite.x + revealDistance;
                break;
        }
        
        await CardAnimations.slideCard(cardSprite, { x: targetX, y: targetY }, 400);

        // Validate sprite before continuing
        if (!cardSprite || cardSprite.destroyed) {
            return null;
        }

        // Step 2: Load the front texture
        const frontTexture = await Assets.load(this.getCardImagePath(card.suit, card.value));

        // Step 3: Flip the card to reveal it
        await CardAnimations.flipCard(cardSprite, frontTexture, 0, 300);

        return {
            card: card,
            sprite: cardSprite
        };
    }

    /**
     * Removes a card from the deck visually
     * @param {number} cardIndex - Index of the card to remove
     * @param {boolean} animate - Whether to animate the removal (default: true)
     */
    async removeCard(cardIndex = 0, animate = true) {
        const cardSprite = this.cardSprites.find(sprite => sprite.deckIndex === cardIndex);

        if (!cardSprite) {
            return;
        }

        if (animate) {
            // Fade out animation
            await this.fadeOutCard(cardSprite);
        }

        // Remove from container
        if (this.container && cardSprite && cardSprite.parent === this.container) {
            this.container.removeChild(cardSprite);
        }

        // Remove from array
        const spriteIndex = this.cardSprites.indexOf(cardSprite);
        if (spriteIndex > -1) {
            this.cardSprites.splice(spriteIndex, 1);
        }

        // Destroy sprite
        if (cardSprite && !cardSprite.destroyed) {
            cardSprite.destroy();
        }
    }

    /**
     * Fades out a card sprite
     * @param {PIXI.Sprite} sprite - The sprite to fade out
     * @param {number} duration - Animation duration in milliseconds
     */
    fadeOutCard(sprite, duration = 300) {
        return new Promise((resolve) => {
            const start = performance.now();
            const startAlpha = sprite.alpha;

            const animate = (time) => {
                const t = Math.min(1, (time - start) / duration);
                sprite.alpha = startAlpha * (1 - t);

                if (t < 1) {
                    requestAnimationFrame(animate);
                } else {
                    sprite.alpha = 0;
                    resolve();
                }
            };

            requestAnimationFrame(animate);
        });
    }

    /**
     * Repositions all remaining cards in the deck with stacking effect
     * Useful after removing cards
     * @param {boolean} animate - Whether to animate the repositioning
     */
    async repositionDeck(animate = true) {
        const duration = 300;
        const promises = [];

        // Sort sprites by their original deck index to maintain order
        const sortedSprites = [...this.cardSprites].sort((a, b) => b.deckIndex - a.deckIndex);

        sortedSprites.forEach((sprite, stackIndex) => {
            // Position relative to container center (0,0)
            const targetX = (stackIndex * this.stackOffsetX);
            const targetY = (stackIndex * this.stackOffsetY);

            if (animate) {
                promises.push(CardAnimations.slideCard(sprite, { x: targetX, y: targetY }, duration));
            } else {
                sprite.x = targetX;
                sprite.y = targetY;
            }
        });

        if (animate) {
            await Promise.all(promises);
        }
    }

    /**
     * Updates the deck renderer (called on each frame if needed)
     */
    update() {
        // Can be used for continuous animations or updates if needed
    }

    /**
     * Cleans up and destroys all sprites
     */
    destroy() {
        if (this.container) {
            this.cardSprites.forEach(sprite => sprite.destroy());
            this.cardSprites = [];
            this.app.stage.removeChild(this.container);
            this.container.destroy();
            this.container = null;
        }
    }

    /**
     * Gets the number of cards currently rendered
     */
    get cardCount() {
        return this.cardSprites.length;
    }

    /**
     * Sets the position of the deck based on the player index
     * Player positions around the table:
     * - Player 0 (Player 1): Bottom side
     * - Player 1 (Player 2): Right side
     * - Player 2 (Player 3): Top side
     * - Player 3 (Player 4): Left side
     * 
     * @param {number} playerIndex - Index of the player (0-3)
     * @param {number} screenWidth - Width of the screen
     * @param {number} screenHeight - Height of the screen
     */
    setPositionByPlayer(playerIndex, screenWidth, screenHeight) {
        const margin = 20; // Distance from edge (same as ScoreMarkerRenderer)

        switch (playerIndex) {
            case 0: // Player 1 - Bottom Right
                // Anchor (0.5, 0.5) - position from bottom-right corner
                this.centerX = screenWidth - margin - this.cardWidth / 2;
                this.centerY = screenHeight - margin * 2 - this.cardHeight;
                this.rotation = 0;
                break;

            case 1: // Player 2 - Bottom Right (rotated 90° clockwise)
                // When rotated, width becomes height and vice versa
                this.centerX = screenWidth - margin - this.cardHeight / 2;
                this.centerY = screenHeight - margin * 2 - this.cardHeight;
                this.rotation = Math.PI / 2;
                break;

            case 2: // Player 3 - Top Left (rotated 180°)
                this.centerX = margin + this.cardWidth / 2;
                this.centerY = margin + this.cardHeight / 2;
                this.rotation = Math.PI;
                break;

            case 3: // Player 4 - Top Left (rotated 90° counter-clockwise)
                // When rotated, width becomes height and vice versa
                this.centerX = margin + this.cardWidth / 2;
                this.centerY = margin * 2;
                this.rotation = -Math.PI / 2;
                break;

            default:
                // Default to center if invalid index
                this.centerX = screenWidth / 2;
                this.centerY = screenHeight / 2;
                this.rotation = 0;
                console.warn(`Invalid player index: ${playerIndex}. Using center position.`);
        }

        // If deck is already rendered, update container transform
        if (this.container) {
            this.container.x = this.centerX;
            this.container.y = this.centerY;
            this.container.rotation = this.rotation;
        }
    }

    /**
     * Sets the position of the deck
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     */
    setPosition(x, y) {
        this.centerX = x;
        this.centerY = y;
        if (this.container) {
            this.container.x = x;
            this.container.y = y;
        }
    }

    /**
     * Sets the card dimensions
     * @param {number} width - Card width
     * @param {number} height - Card height
     */
    setCardSize(width, height) {
        this.cardWidth = width;
        this.cardHeight = height;

        // Update all existing sprites
        this.cardSprites.forEach(sprite => {
            sprite.width = width;
            sprite.height = height;
        });
    }

    /**
     * Sets the stacking offset
     * @param {number} offsetX - Horizontal offset between cards
     * @param {number} offsetY - Vertical offset between cards
     */
    setStackOffset(offsetX, offsetY) {
        this.stackOffsetX = offsetX;
        this.stackOffsetY = offsetY;
        this.repositionDeck(false);
    }

    /**
     * Gets the current deck position
     * @returns {Object} Position {x, y} of the deck
     */
    getDeckPosition() {
        return {
            x: this.centerX,
            y: this.centerY
        };
    }
}

export default DeckRenderer;